import * as XLSX from 'xlsx';
import { RawWellskyHeaders } from '../types/index.js';
import { API_BASE_URL } from '../constants/api';

/**
 * Parses a WellSky Excel file and extracts data from the "Wellsky Raw Data" sheet
 * @param {File} file - Excel file uploaded by user
 * @returns {Promise<Array>} - Array of raw WellSky records
 */
export async function parseWellskyFile(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv' // .csv
    ];

    const fileExtension = file.name.toLowerCase().split('.').pop();
    const validExtensions = ['xlsx', 'xls', 'csv'];

    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      reject(new Error('Invalid file type. Please upload an Excel (.xlsx, .xls) or CSV file.'));
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target.result;
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });

        // Look for the "Wellsky Raw Data" sheet
        const sheetName = 'Wellsky Raw Data';
        
        if (!workbook.SheetNames.includes(sheetName)) {
          // If specific sheet not found, try first sheet
          const firstSheet = workbook.SheetNames[0];
          console.warn(`Sheet "${sheetName}" not found. Using first sheet: "${firstSheet}"`);
          
          if (!firstSheet) {
            reject(new Error('No sheets found in the workbook'));
            return;
          }
          
          const worksheet = workbook.Sheets[firstSheet];
          const data = parseWorksheet(worksheet);
          resolve(data);
        } else {
          const worksheet = workbook.Sheets[sheetName];
          const data = parseWorksheet(worksheet);
          resolve(data);
        }
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        reject(new Error(`Failed to parse file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsArrayBuffer(file);
  });
}

/**
 * Parses a worksheet and validates headers
 * @param {Object} worksheet - XLSX worksheet object
 * @returns {Array} - Array of parsed records
 */
function parseWorksheet(worksheet) {
  // Debug worksheet info
  console.log('Worksheet info:', {
    ref: worksheet['!ref'],
    range: worksheet['!ref'] ? XLSX.utils.decode_range(worksheet['!ref']) : null
  });
  
  // Try multiple approaches to read the data
  let rawData;
  
  // Approach 1: Try with explicit range
  if (worksheet['!ref']) {
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    console.log('Using range:', range);
    rawData = XLSX.utils.sheet_to_json(worksheet, { 
      header: 1,
      range: range,
      defval: '',
      raw: false // Convert everything to strings
    });
  } else {
    // Approach 2: Try without range
    rawData = XLSX.utils.sheet_to_json(worksheet, { 
      header: 1,
      defval: '',
      raw: false
    });
  }
  
  console.log('Raw data length:', rawData.length);
  console.log('First few rows:', rawData.slice(0, 3));
  
  // If we're only getting 3 columns, try a different approach
  if (rawData.length > 0 && rawData[0] && rawData[0].length <= 3) {
    console.log('Only found 3 columns, trying alternative parsing...');
    
    // Try reading with no range specified and different options
    rawData = XLSX.utils.sheet_to_json(worksheet, { 
      header: 1,
      blankrows: false,
      raw: false
    });
    
    console.log('Alternative parsing - Raw data length:', rawData.length);
    console.log('Alternative parsing - First few rows:', rawData.slice(0, 3));
    
    // If still not working, try reading each cell manually
    if (rawData.length > 0 && rawData[0] && rawData[0].length <= 3) {
      console.log('Trying manual cell reading...');
      
      // Get the range and read cells manually
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:Z100');
      const manualData = [];
      
      for (let R = range.s.r; R <= Math.min(range.e.r, 10); R++) { // Read first 10 rows
        const row = [];
        for (let C = range.s.c; C <= range.e.c; C++) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = worksheet[cellAddress];
          row.push(cell ? (cell.v || '') : '');
        }
        manualData.push(row);
      }
      
      rawData = manualData;
      console.log('Manual parsing - Raw data length:', rawData.length);
      console.log('Manual parsing - First few rows:', rawData.slice(0, 3));
    }
  }
  
  if (rawData.length === 0) {
    throw new Error('The worksheet is empty');
  }

  // Find the header row (first non-empty row)
  let headerRowIndex = -1;
  let headers = [];
  
  for (let i = 0; i < rawData.length; i++) {
    const row = rawData[i];
    if (row && row.length > 0 && row.some(cell => cell && cell.toString().trim())) {
      // Check if this looks like a header row
      const potentialHeaders = row.map(cell => cell ? cell.toString().trim() : '');
      
      // Debug: log the potential headers
      console.log('Checking potential headers:', potentialHeaders);
      console.log('Row length:', row.length);
      console.log('Full row:', row);
      
      // Look for key WellSky headers to validate this is the right row
      const keyHeaders = [
        RawWellskyHeaders.DATABASE_ID,
        RawWellskyHeaders.FULL_NAME,
        RawWellskyHeaders.AGENCY_SUBDOMAIN
      ];
      
      console.log('Looking for key headers:', keyHeaders);
      
      const foundKeyHeaders = keyHeaders.filter(keyHeader => 
        potentialHeaders.some(header => 
          header.toLowerCase() === keyHeader.toLowerCase()
        )
      );
      
      console.log('Found key headers:', foundKeyHeaders);
      
      if (foundKeyHeaders.length >= 2) {
        headerRowIndex = i;
        headers = potentialHeaders;
        break;
      }
      
      // If we have a reasonable number of columns and at least one key header, 
      // this might be the right row even if not all key headers are found
      if (potentialHeaders.length >= 10 && foundKeyHeaders.length >= 1) {
        console.log('Using row with partial match - has', potentialHeaders.length, 'columns and', foundKeyHeaders.length, 'key headers');
        headerRowIndex = i;
        headers = potentialHeaders;
        break;
      }
    }
  }
  
  if (headerRowIndex === -1) {
    throw new Error('Could not find valid header row with expected WellSky columns');
  }

  // Validate that we have the required headers
  const requiredHeaders = [
    RawWellskyHeaders.DATABASE_ID,
    RawWellskyHeaders.FULL_NAME,
    RawWellskyHeaders.AGENCY_SUBDOMAIN
  ];

  const missingHeaders = requiredHeaders.filter(required => 
    !headers.some(header => 
      header.toLowerCase() === required.toLowerCase()
    )
  );

  if (missingHeaders.length > 0) {
    console.warn('Some expected headers not found:', missingHeaders);
  }

  // Convert rows to objects using headers
  const records = [];
  
  for (let i = headerRowIndex + 1; i < rawData.length; i++) {
    const row = rawData[i];
    
    // Skip empty rows and placeholder rows
    if (!row || row.length === 0 || 
        row.every(cell => !cell || cell.toString().trim() === '') ||
        row.some(cell => cell && cell.toString().toLowerCase().includes('search homematters'))) {
      continue;
    }

    const record = {};
    
    // Map each cell to its corresponding header
    headers.forEach((header, index) => {
      if (header && row[index] !== undefined) {
        const value = row[index];
        record[header] = value ? value.toString().trim() : '';
      }
    });

    // Only include records that have at least a Database ID or Full Name
    if (record[RawWellskyHeaders.DATABASE_ID] || record[RawWellskyHeaders.FULL_NAME] ||
        Object.values(record).some(val => val && val.toString().trim())) {
      records.push(record);
    }
  }

  if (records.length === 0) {
    throw new Error('No valid data records found in the file');
  }

  console.log(`Successfully parsed ${records.length} WellSky records`);
  return records;
}

/**
 * Validates a WellSky record for required fields
 * @param {Object} record - WellSky record to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export function validateWellskyRecord(record) {
  const errors = [];
  const warnings = [];

  // Check for required fields
  if (!record[RawWellskyHeaders.DATABASE_ID]) {
    errors.push('Missing Database ID');
  }

  if (!record[RawWellskyHeaders.FULL_NAME]) {
    errors.push('Missing Full Name');
  }

  if (!record[RawWellskyHeaders.AGENCY_SUBDOMAIN]) {
    warnings.push('Missing Agency Subdomain');
  }

  // Check date formats
  if (record[RawWellskyHeaders.START_DATE]) {
    const startDate = new Date(record[RawWellskyHeaders.START_DATE]);
    if (isNaN(startDate.getTime())) {
      warnings.push('Invalid Start Date format');
    }
  }

  // Check phone number formats
  const mobilePhone = record[RawWellskyHeaders.MOBILE_PHONE];
  const homePhone = record[RawWellskyHeaders.HOME_PHONE];
  
  if (mobilePhone && !/[\d\s\-()+ ]/.test(mobilePhone)) {
    warnings.push('Invalid Mobile Phone format');
  }
  
  if (homePhone && !/[\d\s\-()+ ]/.test(homePhone)) {
    warnings.push('Invalid Home Phone format');
  }

  // Check email format
  const email = record[RawWellskyHeaders.EMAIL];
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    warnings.push('Invalid Email format');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Gets statistics about the parsed WellSky data
 * @param {Array} records - Array of WellSky records
 * @returns {Object} - Statistics object
 */
export function getWellskyStats(records) {
  if (!records || !Array.isArray(records)) {
    return { total: 0, valid: 0, withWarnings: 0, errors: 0 };
  }

  let valid = 0;
  let withWarnings = 0;
  let errors = 0;

  records.forEach(record => {
    const validation = validateWellskyRecord(record);
    if (validation.isValid) {
      if (validation.warnings.length > 0) {
        withWarnings++;
      } else {
        valid++;
      }
    } else {
      errors++;
    }
  });

  return {
    total: records.length,
    valid,
    withWarnings,
    errors
  };
}

// Backend API configuration
const BACKEND_API_BASE = API_BASE_URL;

/**
 * Uploads WellSky Excel file to backend for processing and database storage
 * @param {File} file - Excel file to upload
 * @returns {Promise<object>} - Result containing processed records and save results
 */
export async function uploadWellskyFile(file) {
  if (!file) {
    throw new Error('No file provided');
  }

  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${BACKEND_API_BASE}/wellsky/upload`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Upload error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Failed to upload WellSky file');
    }

    console.log(`Uploaded and processed ${data.records.length} WellSky records`);
    return {
      records: data.records,
      saveResults: data.saveResults,
      totalCount: data.totalCount,
      importBatch: data.importBatch,
      headerMapping: data.headerMapping,
      fileName: data.fileName
    };
  } catch (error) {
    console.error('Error uploading WellSky file:', error);
    throw error;
  }
}

/**
 * Retrieves saved WellSky records from the database
 * @param {object} params - Query parameters for filtering and pagination
 * @returns {Promise<object>} - Object containing records array and pagination info
 */
export async function getSavedWellskyRecords(params = {}) {
  console.log('🔍 getSavedWellskyRecords called with params:', params);
  
  try {
    const queryParams = new URLSearchParams();
    
    // Add all parameters to query string
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const url = `${BACKEND_API_BASE}/wellsky/records?${queryParams}`;
    console.log('📡 WellSky Service: Fetching from URL:', url);
    console.log('📡 WellSky Service: BACKEND_API_BASE:', BACKEND_API_BASE);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    console.log('📨 WellSky Service: Response status:', response.status, response.statusText);
    console.log('📨 WellSky Service: Response ok:', response.ok);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ WellSky Service: Response not ok:', response.status, errorData);
      throw new Error(errorData.message || `Error fetching saved records: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('📊 WellSky Service: Response data:', data);
    
    if (!data.success) {
      console.error('❌ WellSky Service: Data success false:', data.message);
      throw new Error(data.message || 'Failed to fetch saved WellSky records');
    }

    console.log(`✅ WellSky Service: Retrieved ${data.records?.length || 0} saved records from database`);
    
    const result = {
      success: true,
      records: data.records || [],
      pagination: data.pagination,
      totalCount: data.pagination?.total || data.records?.length || 0
    };
    
    console.log('🎯 WellSky Service: Returning result:', result);
    return result;
  } catch (error) {
    console.error('💥 WellSky Service Error retrieving saved records:', error);
    throw error;
  }
}
